// What each system on the stack list will actually let us read, and where we
// are with each route.
//
// This is not funnel copy, which is why it is here rather than in funnel.ts: it
// is a description of other people's APIs and of our own queue, and it changes
// when we ship an integration, not when someone rewrites a question. The server
// keeps its own view of the same fact — Onboarding::Scoring's support tiers —
// because how much a stack is worth to us is a business rule, not a screen.
//
// `live` is a route a lead can take today. `planned` is one the system supports
// and we have not built; it is shown rather than hidden, because a greyed
// "Coming soon" tells them we know it exists, and a missing button does not.
// Absent means the system has no such route at all — Intuit does not issue API
// keys to third parties, so QuickBooks has no key row and never will.

export type Route = "live" | "planned";

export type Source = {
  // OAuth, where the system has it. The two live ones are the two the app has
  // apps registered for.
  oauth?: Route;
  // A key or token pasted in. Read-only wherever the system offers that grade.
  apiKey?: Route;
  keyHint?: string;
  // Every system that can be exported can be uploaded. QuickBooks is the one
  // exception: its export is a report rather than a ledger, so the connection
  // is the only route worth taking.
  csv: boolean;
  csvHint?: string;
  // Anything true of this system and no other.
  note?: string;
};

export const SOURCES: Record<string, Source> = {
  // --- accounting ---------------------------------------------------------
  QUICKBOOKS: {
    oauth: "live",
    csv: false,
    note: "Read-only. We pull your chart of accounts, vendors and transactions, and nothing we do writes back.",
  },
  XERO: {
    oauth: "planned",
    csv: true,
    csvHint: "Accounting → Reports → Account Transactions, then Export.",
  },
  SAGE: {
    csv: true,
    csvHint: "Reporting → Transactions, then Export to CSV.",
    note: "Sage's API differs by product, so the export is the route that works for every version of it.",
  },

  // --- money in -----------------------------------------------------------
  STRIPE: {
    oauth: "live",
    apiKey: "live",
    keyHint:
      "A restricted key (rk_…) with read access. Developers → API keys → Create restricted key.",
    csv: true,
    csvHint: "Balance → Payouts → Export, or the Payments export.",
    note: "Connecting reads your charge and payout history straight away, which is where the first numbers we show you come from.",
  },
  SHOPIFY: {
    oauth: "planned",
    csv: true,
    csvHint: "Finances → Payouts → Export, and Orders → Export.",
  },
  PAYPAL: {
    apiKey: "planned",
    keyHint: "A client ID and secret from a REST app with read-only scopes.",
    csv: true,
    csvHint: "Activity → Statements → Custom transactions, downloaded as CSV.",
  },
  SQUARE: {
    oauth: "planned",
    apiKey: "planned",
    keyHint: "A personal access token with read-only scopes.",
    csv: true,
    csvHint: "Reports → Transactions or Transfers, then Export.",
  },
  PADDLE: {
    apiKey: "planned",
    keyHint: "An API key from Developer tools → Authentication.",
    csv: true,
    csvHint: "Transactions → Export, or the payouts export.",
    note: "Paddle is your merchant of record, so its payouts are net of fees and tax — we unpick that for you.",
  },
  CHARGEBEE: {
    apiKey: "planned",
    keyHint: "Your site name and a read-only API key.",
    csv: true,
    csvHint: "Exports → Transactions, or the revenue export.",
  },

  // --- banks and cards ----------------------------------------------------
  US_CHASE: {
    csv: true,
    csvHint: "Statements → Download, or Activity → Export as CSV.",
    note: "No bank in the US opens itself to us directly without an aggregator sitting in the middle. A statement export is the whole picture and costs you a minute.",
  },
  MERCURY: {
    apiKey: "planned",
    keyHint: "A read-only API token from Settings → API tokens.",
    csv: true,
    csvHint: "Transactions → Export, or the monthly statement.",
  },
  REVOLUT: {
    csv: true,
    csvHint: "Accounts → Statement → Excel/CSV, any date range.",
    note: "Revolut's business API needs a certificate exchange to set up, so the export is the honest route for now.",
  },
  BREX: {
    apiKey: "planned",
    keyHint: "A user token with read-only scopes.",
    csv: true,
    csvHint: "Transactions → Export as CSV.",
  },
  RAMP: {
    apiKey: "planned",
    keyHint: "A client ID and secret with read-only scopes.",
    csv: true,
    csvHint: "Transactions → Export, or the accounting export.",
  },

  // --- people and bills ---------------------------------------------------
  WISE: {
    csv: true,
    csvHint: "Balances → Statement → CSV, any date range, any currency.",
    note: "Multi-currency is where drift hides, so give us every balance you hold rather than only the one you think in.",
  },
  GUSTO: {
    oauth: "planned",
    csv: true,
    csvHint: "Reports → Payroll journal, exported as CSV.",
  },
  RIPPLING: {
    apiKey: "planned",
    keyHint: "An API key with read-only scopes.",
    csv: true,
    csvHint: "Reports → Payroll register, exported as CSV.",
  },
  DEEL: {
    csv: true,
    csvHint: "Invoices → Export, or the payments report.",
    note: "One Deel payment covers many contractors. The export names them, which is what lets us split the payment back out.",
  },
  BILL_COM: {
    apiKey: "planned",
    keyHint:
      "An API key and org ID — Bill.com gates these behind partner access.",
    csv: true,
    csvHint: "Reports → Bills or Payments, exported as CSV.",
  },

  // --- the honest answer --------------------------------------------------
  SPREADSHEETS: {
    csv: true,
    csvHint: "Whatever you keep it in. One row per transaction is plenty.",
    note: "No format to match and nothing to tidy first — we would rather read your sheet as it is than have you rebuild it for us.",
  },
};

// Anything the stack list offers that this table has not been told about yet
// still gets a screen: an export is a route for everything.
export const FALLBACK: Source = {
  csv: true,
  csvHint: "Any CSV export works — any date range, any columns.",
};

export function sourceFor(id: string): Source {
  return SOURCES[id] ?? FALLBACK;
}
